import { useCallback, useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { PaymentsPayoutService } from "@/modules/dashboard/dispersions/services/paymentsPayout.service";
import { notificationsSocketService } from "@/modules/dashboard/notifications/services/notifications-socket.service";
import { PaymentsPayinService } from "@/modules/dashboard/payins/services/paymentsPayin.service";

import { DETAIL_POLL_INTERVAL_MS } from "../constants/paymentsActivity.constants";
import {
  PaymentsStatusChangesService,
  type PaymentStatusChange,
} from "../services/paymentsStatusChanges.service";
import type { ActivityRow } from "../types/paymentsActivity.types";
import { adaptPayinRow, adaptPayoutRow } from "../utils/activityAdapters";

/**
 * The timeline read, tagged with what it was read for. Both parts of the tag matter: the operation, so
 * another one's transitions never show, and the status, so a flip is known to need a refetch.
 */
interface TimelineSnapshot {
  key: string;
  status: string | null;
  data: PaymentStatusChange[] | null;
}

export interface UseOperationDetailResult {
  /** Live copy of the row — starts as the list row and is refreshed by `GET /:id` while non-terminal. */
  row: ActivityRow;
  /**
   * Real transition timeline (`GET /internal/payments/status-changes`) — INTERNAL staff only; stays null for
   * org members (the endpoint 403s them), for whom the drawer derives a timeline from entity timestamps.
   */
  timeline: PaymentStatusChange[] | null;
  isTimelineLoading: boolean;
}

/**
 * Detail-drawer live state for one runtime operation. While the operation is non-terminal it polls the
 * org-scoped detail endpoint every 6 s (stops on terminal — the backend enforces the transition table, so a
 * terminal status can never move again). The timeline is fetched only for INTERNAL users and refreshed when
 * the status flips so the new transition appears.
 */
export const useOperationDetail = (initialRow: ActivityRow): UseOperationDetailResult => {
  const { isInternalUser } = useAuth();
  const [detail, setDetail] = useState<{ key: string; row: ActivityRow } | null>(null);
  const [timelineState, setTimelineState] = useState<TimelineSnapshot | null>(null);

  /**
   * Which operation is open, and the detail read of it — derived, not synchronized: opening a different
   * operation makes the stored key stop matching, so the row falls back to that operation's list row with no
   * effect resetting anything.
   *
   * Keying on the operation rather than the row object matters. The list behind the drawer re-fetches on a
   * timer and hands down a fresh object for the SAME operation every time; treating that as "a different
   * operation" would discard what the detail read added, and the fields only the detail carries — the
   * collection QR above all — would blink out every list refresh.
   */
  const operationKey = `${initialRow.direction}|${initialRow.id}`;
  const row = detail?.key === operationKey ? detail.row : initialRow;
  const setRow = useCallback(
    (next: ActivityRow) => setDetail({ key: `${next.direction}|${next.id}`, row: next }),
    [],
  );

  // Read GET /:id on open, then poll while non-terminal; a settled Bre-B operation is read once, a payin for
  // its origin and a payout for its recipient.
  const readsSettledDetail = row.rail === "BREB";
  useEffect(() => {
    if (!row.isNonTerminal && !readsSettledDetail) return;
    let cancelled = false;
    const read = async () => {
      try {
        const next =
          row.direction === "payins"
            ? adaptPayinRow((await PaymentsPayinService.getPayin(row.id)).data)
            : adaptPayoutRow((await PaymentsPayoutService.getPayout(row.id)).data);
        if (!cancelled) setRow(next);
      } catch {
        // Transient read error — keep the last known state and keep trying.
      }
    };
    // Once immediately, not only on the interval: the detail response carries fields the list one does not —
    // the collection QR above all — so waiting out a full interval would show them appearing seconds late.
    void read();
    const interval = row.isNonTerminal ? setInterval(() => void read(), DETAIL_POLL_INTERVAL_MS) : null;
    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
  }, [row.direction, row.id, row.isNonTerminal, readsSettledDetail, setRow]);

  // The same read, triggered by the socket instead of the clock: the drawer reflects a settle the moment
  // it lands, and the poll above stays as the fallback.
  useEffect(() => {
    const onFlip = (payload: { id: string }) => {
      if (payload.id !== row.id) return;
      void (async () => {
        try {
          const next =
            row.direction === "payins"
              ? adaptPayinRow((await PaymentsPayinService.getPayin(row.id)).data)
              : adaptPayoutRow((await PaymentsPayoutService.getPayout(row.id)).data);
          setRow(next);
        } catch {
          // The poll retries; a failed socket-triggered read must not break the drawer.
        }
      })();
    };
    const offPayin = notificationsSocketService.onPayinUpdate(onFlip);
    const offPayout = notificationsSocketService.onPayoutUpdate(onFlip);
    return () => {
      offPayin();
      offPayout();
    };
  }, [row.direction, row.id, setRow]);

  // Real timeline — INTERNAL only (org members would get 403; they see the derived timeline instead).
  // Refetches on status flips so the newly appended transition shows up.
  useEffect(() => {
    if (!isInternalUser) return;
    const snapshotKey = operationKey;
    const snapshotStatus = row.status;
    let cancelled = false;
    PaymentsStatusChangesService.listByOperation(
      initialRow.direction === "payins" ? "PAYIN" : "PAYOUT",
      initialRow.id,
    )
      .then((res) => {
        if (!cancelled) setTimelineState({ key: snapshotKey, status: snapshotStatus, data: res.data });
      })
      .catch(() => {
        // Timeline is an enhancement — on failure the drawer falls back to the derived one.
        if (!cancelled) setTimelineState({ key: snapshotKey, status: snapshotStatus, data: null });
      });
    return () => {
      cancelled = true;
    };
  }, [isInternalUser, operationKey, initialRow.direction, initialRow.id, row.status]);

  /**
   * Loading is derived from whether the snapshot matches what is on screen, instead of a flag an effect
   * writes: the fetch is keyed by operation AND status, so a status flip reads as "loading again" the moment
   * the status changes, with no render in between where it claims to be up to date.
   *
   * The data is scoped to the operation, so opening another one shows no timeline rather than the previous
   * operation's while its own request is in flight. Within one operation the last result stays on screen
   * while a flip refetches — a timeline that blanks out on every transition would be worse than a slightly
   * stale one.
   */
  const isCurrentOperation = timelineState?.key === operationKey;
  return {
    row,
    timeline: isCurrentOperation ? timelineState.data : null,
    isTimelineLoading: isInternalUser && (!isCurrentOperation || timelineState.status !== row.status),
  };
};
